import Link from "next/link"
import { twMerge } from "tailwind-merge"
import { Heading } from "../../../../ui/heading/Heading"

type ChartHeadingProps = {
  showTitle: boolean
  showDescription: boolean
  title: string
  description: React.ReactNode
  headingImageSlot?: React.ReactNode
  includeSpaceForActions?: boolean
  className?: string
  menuSlot?: React.ReactNode
  logoLayout?: "inline" | "stacked"
  titleSize?: "small" | "large"
  fontFamily?: "sans-serif" | "serif"
  headingHref?: string
}

export function ChartHeading({
  showTitle,
  showDescription,
  title,
  description,
  headingImageSlot,
  className,
  menuSlot,
  logoLayout = "inline",
  titleSize = "small",
  fontFamily,
  headingHref,
}: ChartHeadingProps) {
  // Chart titles sit under the page h1, so they never render as h1 themselves
  let heading = (
    <Heading
      level={titleSize === "large" ? 2 : 4}
      className={twMerge(
        "leading-none! text-pretty",
        titleSize === "large" && "tracking-tighter"
      )}
    >
      {title}
    </Heading>
  )
  if (fontFamily === "serif") {
    heading = (
      <Heading
        level={1}
        as={titleSize === "large" ? "h2" : "h4"}
        className={twMerge(
          "leading-none! text-pretty font-serif tracking-tight",
          titleSize === "large" ? "text-3xl" : "text-xl"
        )}
      >
        {title}
      </Heading>
    )
  }

  // Wrap heading in Link if headingHref is provided
  if (headingHref && showTitle) {
    heading = (
      <Link href={headingHref} className="hover:opacity-80 transition-opacity">
        {heading}
      </Link>
    )
  }

  return showTitle || showDescription || headingImageSlot || menuSlot ? (
    <div className="flex items-start gap-2">
      <div
        className={twMerge(
          "flex gap-3 min-w-0 flex-1 shrink",
          logoLayout === "stacked" ? "flex-col items-start" : "items-center",
          className
        )}
      >
        {headingImageSlot}
        {(showTitle || showDescription) && (
          <div className="flex flex-col gap-2 w-full">
            {showTitle && heading}
            {showDescription && (
              <p className="text-sm text-fg-secondary leading-none!">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
      {menuSlot}
    </div>
  ) : null
}
